import { type lineTypes, SHURDataBlockFactory } from './SHURDataBlockFactory'
import {
  type Contract,
  type GEBLine,
  type HALine,
  HRKeys,
  type HRLine,
  type Pair,
  ProductSubType,
  ProductType,
  RSKeys,
  type RSLine,
  type ShurContract,
  type TRLine,
  UNKeys,
  type UNLine
} from 'shur-api'
import type { DataBlock, IconDataSpan, SingleBlock } from 'de-zurich-ui-library'
import { shurInfosToScreen } from '@/utils/shur/ShurPolicyTile.ScreenBindingsTitleIcon'

import { t } from '@/i18n'
import { isTrue } from '@/utils/CommonUtils'
import { findValueByKey } from '@/utils/shur/SHURDataCreatorHelpers'

export function sortHALines(lines: HALine[]): HALine[] {
  const orderHALines: ProductSubType[] = [
    'LIABILITY_PRIVATE',
    'LIABILITY_PET',
    'LIABILITY_SPORTS_BOAT',
    'LIABILITY_PROPERTY_OWNERS',
    'LIABILITY_WATER_POLLUTION'
  ]

  return lines.sort(function (a: HALine, b: HALine) {
    return orderHALines.indexOf(a.productSubType) - orderHALines.indexOf(b.productSubType)
  })
}

export function sortRSLinesByTitle(lines: DataBoxWithHeader[]): DataBoxWithHeader[] {
  const orderRSLines: string[] = [
    t('shurDetails.sections.LEGAL.PrivAndProfTitle').toString(),
    t('shurDetails.sections.LEGAL.FamTrafTitle').toString(),
    t('shurDetails.sections.LEGAL.PropertyTitle').toString(),
    t('shurDetails.sections.LEGAL.ExtCriminalTitle').toString(),
    t(`shurDetails.sections.LEGAL.${RSKeys.RealestateOwnerLegal}`).toString(),
    t(`shurDetails.sections.LEGAL.${RSKeys.RealestateTenantLegal}`).toString(),
    t(`shurDetails.sections.LEGAL.${RSKeys.RealestateLandlordLegal}`).toString()
  ]

  return lines.sort(function (a: DataBoxWithHeader, b: DataBoxWithHeader) {
    return orderRSLines.indexOf(a.title) - orderRSLines.indexOf(b.title)
  })
}

/**
 * This function create a whole bundle of details cards incl. Header (Title and Subtitle) and DataSections (incl.
 * DataLines)
 * Used only for HA-Line - Liability
 * @param lines
 * @param isBundle
 */
function createHACard(lines: HALine[], isBundle: boolean): DataBoxWithHeader[] {
  const HACards: DataBoxWithHeader[] = []

  lines.forEach((line: lineTypes) => {
    // Creates a bunch of DataSections (incl. DataLines)
    const dataSection: DataBlock[] = new SHURDataBlockFactory().createDataBlockForLine(
      line
    )

    // get Title from Type and SubType
    const screenData = getProductData('LIABILITY', line)

    let title: string = t('shurDetails.collapserTitle').toString()

    if (isBundle || lines.length > 1) {
      // specific title is only needed in case of bundle
      title = screenData.firstTitle
    }

    const fullCard: DataBoxWithHeader = {
      title: title,
      subtitle: '',
      datablock: removeEmptySections(removeEmptyValues(dataSection)),
      isFallback: false
    }

    HACards.push(fullCard)
  })

  return HACards
}

/**
 * This function create a whole bundle of details cards incl. Header (Title and Subtitle) and DataSections (incl.
 * DataLines)
 * Used only for UN-Line - Accident
 * @param lines
 * @param isBundle
 */
function createUNCard(lines: UNLine[], isBundle: boolean): DataBoxWithHeader[] {
  const UNCards: DataBoxWithHeader[] = []

  lines.forEach((line: UNLine) => {
    if (!line.insuredPersons || line.insuredPersons.length === 0) return []
    line.insuredPersons.forEach((person) => {
      // Creates a bunch of DataSections (incl. DataLines)
      const dataSection: DataBlock[] = new SHURDataBlockFactory().createDataBlockForLine(
        line,
        person
      )

      // get Title from Type and SubType
      const screenData = getProductData('ACCIDENT', line)

      let title: string = t('shurDetails.collapserTitle').toString()
      let subTitle = ''

      if (isBundle) {
        // specific title is only needed in case of bundle
        title = screenData.firstTitle
      }
      if (line.insuredPersons!.length > 1) subTitle = extractedFullname(person.details!)

      const fullCard: DataBoxWithHeader = {
        title: title,
        subtitle: subTitle,
        datablock: removeEmptySections(removeEmptyValues(dataSection)),
        isFallback: false
      }

      UNCards.push(fullCard)
    })
  })

  return UNCards
}

/**
 * This function create a whole bundle of details cards incl. Header (Title and Subtitle) and DataSections (incl.
 * DataLines)
 * Used only for HR-Line - Householdcontents
 * @param lines
 * @param isBundle
 */
function createHRCard(lines: HRLine[], isBundle: boolean): DataBoxWithHeader[] {
  const HRCards: DataBoxWithHeader[] = []

  lines.forEach((line: HRLine) => {
    if (!line.insuredHousehold || line.insuredHousehold.length === 0) return []
    line.insuredHousehold.forEach((household) => {
      // Creates a bunch of DataSections (incl. DataLines)
      const dataSection: DataBlock[] = new SHURDataBlockFactory().createDataBlockForLine(
        line,
        household
      )

      // get Title from Type and SubType
      const screenData = getProductData('HOUSEHOLDCONTENTS', line)

      let title: string = t('shurDetails.collapserTitle').toString()
      let subTitle = ''

      if (isBundle) {
        // specific title is only needed in case of bundle
        title = screenData.firstTitle
      }
      if (line.insuredHousehold!.length > 1)
        subTitle = extractedFullAddress(household.details!)

      const fullCard: DataBoxWithHeader = {
        title: title,
        subtitle: subTitle,
        datablock: removeEmptySections(removeEmptyValues(dataSection)),
        isFallback: false
      }

      HRCards.push(fullCard)
    })
  })

  return HRCards
}

/**
 * This function create a whole bundle of details cards incl. Header (Title and Subtitle) and DataSections (incl.
 * DataLines)
 * Used only for GEB-Line - Building (Wohngebäude)
 * @param lines
 * @param isBundle
 */
function createGEBCard(lines: GEBLine[], isBundle: boolean): DataBoxWithHeader[] {
  const GEBCards: DataBoxWithHeader[] = []
  lines.forEach((line: GEBLine) => {
    // Creates a bunch of DataSections (incl. DataLines)
    if (!line) return []

    const dataSection: DataBlock[] = new SHURDataBlockFactory().createDataBlockForLine(
      line as any
    )

    // get Title from Type and SubType
    const screenData = getProductData(ProductType.Building, line as any)

    let title: string = t('shurDetails.collapserTitle').toString()

    // specific title is only needed in case of bundle
    // KPTWO-6526: the title will not be changed in case of single GEB with multiple places
    if (isBundle) {
      title = screenData.firstTitle
    }

    const subtitleStreetAndNumber = line.details
      ? (findValueByKey(line.details, 'STREET_AND_NUMBER') ?? '')
      : ''

    const fullCard: DataBoxWithHeader = {
      title: title,
      subtitle: subtitleStreetAndNumber,
      datablock: removeEmptySections(removeEmptyValues(dataSection)),
      isFallback: false
    }

    GEBCards.push(fullCard)
  })

  return GEBCards
}

/**
 * This function create a whole bundle of details cards incl. Header (Title and Subtitle) and DataSections (incl.
 * DataLines)
 * Used only for RS-Line - Householdcontents
 * @param lines
 * @param isBundle
 */
function createRSCard(lines: RSLine[], isBundle: boolean): DataBoxWithHeader[] {
  const RSCards: DataBoxWithHeader[] = []
  lines.forEach((line: RSLine) => {
    // Creates a bunch of DataSections (incl. DataLines)
    if (!line) return []

    const dataSection: DataBlock[] = new SHURDataBlockFactory().createDataBlockForLine(
      line
    )

    const title: string = getRSTitle(line, isBundle, lines.length)

    const fullCard: DataBoxWithHeader = {
      title: title,
      subtitle: '',
      datablock: removeEmptySections(removeEmptyValues(dataSection)),
      isFallback: false
    }
    RSCards.push(fullCard)
  })

  return sortRSLinesByTitle(RSCards)
}

/**
 * This function returns the correct title for RS lines depending on subtype or details, bundle and line count
 * @param rsLine
 * @param isBundle
 * @param linesLength
 */
function getRSTitle(rsLine: RSLine, isBundle: boolean, linesLength: number): string {
  if (linesLength > 1 || isBundle) {
    if (rsLine.productSubType === 'LEGAL_FAMILY_TRAFFIC')
      return t('shurDetails.sections.LEGAL.FamTrafTitle').toString()
    if (rsLine.productSubType === 'LEGAL_PRIVATE_AND_PROFESSIONAL')
      return t('shurDetails.sections.LEGAL.PrivAndProfTitle').toString()
    if (rsLine.productSubType === 'LEGAL_PROPERTY')
      return t('shurDetails.sections.LEGAL.PropertyTitle').toString()
    if (rsLine.productSubType === 'LEGAL_EXTENDED_CRIMINAL')
      return t('shurDetails.sections.LEGAL.ExtCriminalTitle').toString()
    if (rsLine.details?.find((entry) => entry.key === RSKeys.RealestateOwnerLegal)?.value)
      return t(`shurDetails.sections.LEGAL.${RSKeys.RealestateOwnerLegal}`).toString()
    if (
      rsLine.details?.find((entry) => entry.key === RSKeys.RealestateTenantLegal)?.value
    )
      return t(`shurDetails.sections.LEGAL.${RSKeys.RealestateTenantLegal}`).toString()
    if (
      rsLine.details?.find((entry) => entry.key === RSKeys.RealestateLandlordLegal)?.value
    )
      return t(`shurDetails.sections.LEGAL.${RSKeys.RealestateLandlordLegal}`).toString()
  }
  return t('shurDetails.collapserTitle').toString()
}

/**
 * This function create a whole bundle of details cards incl. Header (Title and Subtitle) and DataSections (incl.
 * DataLines)
 * Used only for TR-Line - Transport
 * @param contract
 * @param lines
 * @param isBundle
 */
function createTRCard(
  contract: Contract,
  lines: TRLine[],
  isBundle: boolean
): DataBoxWithHeader[] {
  const TRCards: DataBoxWithHeader[] = []
  lines.forEach((line: TRLine) => {
    // Creates a bunch of DataSections (incl. DataLines)
    if (!line) return

    // Special handling for TRANSPORT/UNKNOWN: create fallback card with specific text
    if (
      line.productSubType === 'UNKNOWN' &&
      contract.productType === ProductType.Transport
    ) {
      const fullCard: DataBoxWithHeader = {
        title: t('shurDetails.sections.TRANSPORT_UNKNOWN.title').toString(),
        subtitle: '',
        content: t('shurDetails.sections.TRANSPORT_UNKNOWN.content').toString(),
        datablock: [],
        isFallback: true
      }
      TRCards.push(fullCard)
      return
    }

    /*const dataSection: DataBlock[] = new SHURDataBlockFactory().createDataBlockForLine(
      line
    )*/

    const dataSection: DataBlock[] =
      new SHURDataBlockFactory().createDataBlockForContract(contract, line)

    let title: string = t('shurDetails.collapserTitle').toString()

    if (lines.length > 1 || isBundle) {
      if (line.productSubType === 'TRAVEL_COST')
        title = 'Reiserücktrittskosten-Versicherung'
    }

    const fullCard: DataBoxWithHeader = {
      title: title,
      subtitle: '',
      datablock: removeEmptySections(removeEmptyValues(dataSection)),
      isFallback: false
    }
    TRCards.push(fullCard)
  })

  return TRCards
}

function createFallbackCard(): DataBoxWithHeader[] {
  const fullCard: DataBoxWithHeader = {
    title: t('shurDetails.errorNoDetailsTitle').toString(),
    subtitle: '',
    content: t('shurDetails.errorNoDetails').toString(),
    datablock: [],
    isFallback: true
  }

  return [fullCard]
}

export function contractToDataboxData(
  contract: Contract,
  isBundle: boolean
): DataBoxWithHeader[] {
  const result: DataBoxWithHeader[] = []
  const c = contract as any

  const lineConfigs: LineConfig<any>[] = [
    { line: contract.unLine, createCard: createUNCard },
    { line: contract.haLine, createCard: createHACard, sort: sortHALines },
    { line: contract.hrLine, createCard: createHRCard },
    { line: c.gebLine, createCard: createGEBCard },
    { line: contract.rsLine, createCard: createRSCard },
    {
      line: contract.trLine,
      createCard: (line, b) => createTRCard(contract, line, b)
    }
  ]

  lineConfigs.forEach((config) => pushCards(config, isBundle, result))

  const hasBuildingData = c.gebLine && c.gebLine.length > 0
  const isBuildingContract = contract.productType === ProductType.Building

  if (
    contract.showFallback &&
    !hasTransportUnknownCard(result) &&
    !(isBuildingContract && hasBuildingData)
  ) {
    result.push(...createFallbackCard())
  }

  postProcessCards(result, contract)

  return result.length > 0 ? result : []
}

// Helper functions, constants and types
type LineConfig<T> = {
  line: T[] | undefined
  createCard: (line: T[], isBundle: boolean) => DataBoxWithHeader[]
  sort?: (line: T[]) => T[]
}

function pushCards<T>(
  config: LineConfig<T>,
  isBundle: boolean,
  target: DataBoxWithHeader[]
): void {
  const line = config.line
  if (line && line.length > 0) {
    const sorted = config.sort ? config.sort(line) : line
    target.push(...config.createCard(sorted, isBundle))
  }
}

function hasTransportUnknownCard(cards: DataBoxWithHeader[]): boolean {
  return cards.some(
    (card) =>
      card.isFallback &&
      card.title === t('shurDetails.sections.TRANSPORT_UNKNOWN.title').toString()
  )
}

function postProcessCards(cards: DataBoxWithHeader[], contract: Contract): void {
  const productSubLine = (contract as any).productSubLine as string | undefined

  cards.forEach((card) => {
    if (
      card.isFallback &&
      card.title === t('shurDetails.sections.TRANSPORT_UNKNOWN.title').toString()
    ) {
      return
    }
    if (card.datablock[0]?.title?.label === 'noDetailsAvailable') {
      card.content = t('shurDetails.errorNoDetails').toString()
      card.datablock = []
    }
    if (productSubLine && !card.subtitle) {
      card.subtitle = productSubLine
    }
  })
}

function getProductData(productType: ProductType, line: lineTypes) {
  return shurInfosToScreen({
    productName: '',
    productType: productType,
    productSubType: line.productSubType ? line.productSubType : 'UNKNOWN'
  })
}

function extractedFullname(contractDetailsCodeValuePairs: Array<Pair>) {
  return contractDetailsCodeValuePairs.some((entry) => entry.key === UNKeys.LastName) &&
    contractDetailsCodeValuePairs.some((entry) => entry.key === UNKeys.FirstName)
    ? contractDetailsCodeValuePairs.find((entry) => entry.key === UNKeys.FirstName)!
        .value +
        ' ' +
        contractDetailsCodeValuePairs.find((entry) => entry.key === UNKeys.LastName)!
          .value
    : ''
}

function extractedFullAddress(contractDetailsCodeValuePairs: Array<Pair>) {
  return contractDetailsCodeValuePairs.some(
    (entry) => entry.key === HRKeys.StreetAndNumber
  )
    ? 'Ort: ' +
        contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.StreetAndNumber
        )!.value
    : ''
}

function removeEmptyValues(blocks: DataBlock[]): DataBlock[] {
  return blocks
    .map((block) => ({
      ...block,
      data: block.data.filter((singleBlock: SingleBlock) => !!singleBlock.value)
    }))
    .filter((block: DataBlock) => block.title?.label !== '')
}

function removeEmptySections(blocks: DataBlock[]): DataBlock[] {
  return blocks.filter((block: DataBlock, idx) =>
    idx === 0 || block.title?.label === 'noDetailsAvailable'
      ? true
      : block.data.length != 0
  )
}

export async function isPrivatSchutz(policy: ShurContract | null): Promise<boolean> {
  // when contract is NOT PrivatSchutz, return empty to hide this
  return policy?.productName === 'PrivatSchutz'
}

const covConcAllowedValues = new Set([
  'tc_basis',
  'tc_bronze',
  'tc_comfort',
  'tc_gold',
  'tc_luxery',
  'tc_plus',
  'tc_secjura',
  'tc_secjuraplus',
  'tc_silver',
  'tc_standard',
  'tc_top',
  'tc_unknown',
  'tc_pm_gold',
  'tc_tf_gold',
  'tc_cpc_silver',
  'tc_sc_silver',
  'tc_pm_silver',
  'tc_stc_silver',
  'tc_tf_silver',
  'tc_pm_bronze',
  'tc_tf_bronze',
  'tc_pm_migration',
  'tc_ot_migration',
  'tc_sr_migration',
  'tc_tf_migration'
])

export const coverageConceptIconData = (
  coverageConceptType: string,
  shurSubLine: ProductSubType,
  productVersion: string
): IconDataSpan | undefined => {
  const sanitisedCoverageConceptType = covConcAllowedValues.has(coverageConceptType)
    ? coverageConceptType
    : ''
  const sanitisedShurSubLine = shurSubLine.replaceAll(/[<>&]/gim, '')
  const link: string =
    'downloadCoverageConceptPDF|' +
    sanitisedCoverageConceptType +
    '|' +
    sanitisedShurSubLine +
    '|' +
    productVersion

  return isTrue(globalThis.ENV_CONFIG?.FEATURE_FLAGS.SHUR.COVERAGE_CONCEPT_PDF)
    ? {
        iconClass: 'icon-pdf',
        href: encodeURIComponent(link),
        isRouterLink: false
      }
    : undefined
}

export type DataBoxWithHeader = {
  title: string
  subtitle: string
  datablock: DataBlock[]
  isFallback: boolean

  content?: string
}
